import React from 'react';
import styled, { css } from 'styled-components';
import {
  GradientConfidenceText,
  GradientScoreText,
  MarkdownWrapper,
  StickyHeaderModal,
  ScoreValueRow,
  ScoreMax,
  ModalTitleContainer,
  ModalTitleMain,
  ModalTitleCaption,
} from './common';
import { InfoIcon, TransformedSkill } from './ResultsSkillsBreakdown';
import { SmallPortalTooltip } from '../../Tooltip';
import { TooltipContent, TooltipDetail } from '../Progress/Tooltip';
import { AssessmentTestSessionDetailResponse } from '../../../api/admin';

const MetricBoxValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.5px;
  text-transform: capitalize;
`;

const ModalContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 16px;
`;

// Enhanced Prominent Metrics Card
const ProminentMetricsCard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding: 28px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 12px;
`;

const MetricBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetricBoxLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textRegular};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  opacity: 0.8;
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface ResultSkillDetailModalProps {
  skill: TransformedSkill | null;
  test: AssessmentTestSessionDetailResponse;
  onClose: () => void;
  objectiveToScenarios: Map<
    string,
    Array<{ scenarioLabel: string; questionNumber: number; questionId: string }>
  >;
  onQuestionClick: (questionId: string, scenarioLabel: string) => void;
}

// Helper function to get objective description from test steps
function getObjectiveDescription(
  objectiveId: string,
  test: AssessmentTestSessionDetailResponse
): string {
  // Look up the objective in test.steps[].objectives[]
  for (const step of test.steps || []) {
    const objective = step.objectives?.find((obj) => obj.id === objectiveId);
    if (objective?.description) {
      return objective.description;
    }
  }
  // Fallback: format the ID as a readable name
  const parts = objectiveId.split(':');
  if (parts.length < 2) return objectiveId;
  return parts[1]
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper function to build chronological objectives with scenario mapping
function buildObjectivesChronological(
  skill: TransformedSkill,
  test: AssessmentTestSessionDetailResponse,
  objectiveToScenarios: Map<
    string,
    Array<{ scenarioLabel: string; questionNumber: number; questionId: string }>
  >
) {
  const allObjectives: Array<{
    id: string;
    description: string;
    status: 'exceeded' | 'met' | 'partial' | 'not_met' | 'not_tested';
    testedIn: Array<{
      scenarioLabel: string;
      questionNumber: number;
      questionId: string;
    }>;
  }> = [];

  // Process exceeded objectives
  skill.objectivesByStatus.exceeded.forEach((id) => {
    allObjectives.push({
      id,
      description: getObjectiveDescription(id, test),
      status: 'exceeded',
      testedIn: objectiveToScenarios.get(id) || [],
    });
  });

  // Process met objectives
  skill.objectivesByStatus.met.forEach((id) => {
    allObjectives.push({
      id,
      description: getObjectiveDescription(id, test),
      status: 'met',
      testedIn: objectiveToScenarios.get(id) || [],
    });
  });

  // Process partial objectives
  skill.objectivesByStatus.partial.forEach((id) => {
    allObjectives.push({
      id,
      description: getObjectiveDescription(id, test),
      status: 'partial',
      testedIn: objectiveToScenarios.get(id) || [],
    });
  });

  // Process not met objectives
  skill.objectivesByStatus.notMet.forEach((id) => {
    allObjectives.push({
      id,
      description: getObjectiveDescription(id, test),
      status: 'not_met',
      testedIn: objectiveToScenarios.get(id) || [],
    });
  });

  // Process not tested objectives - these also have scenarios where they appear
  skill.objectivesByStatus.notTested.forEach((id) => {
    allObjectives.push({
      id,
      description: getObjectiveDescription(id, test),
      status: 'not_tested',
      testedIn: objectiveToScenarios.get(id) || [],
    });
  });

  return allObjectives;
}

const ModalScoreValue = styled(GradientScoreText)`
  font-size: 24px;
  letter-spacing: -0.5px;
`;

const ModalLevelValue = styled(MetricBoxValue)`
  /* Keep default styling for level */
`;

const ModalConfidenceValue = styled(GradientConfidenceText)`
  font-size: 24px;
  letter-spacing: -0.5px;
`;

const PercentageSign = styled(ScoreMax)`
  /* Inherits from ScoreMax for consistent styling */
`;

// Insights Grid - 2 columns at bottom
const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;
`;

const InsightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
`;

const InsightTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InsightIcon = styled.i`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  opacity: 0.9;
`;

const ObjectivesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitleWithIcon = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

// Chronological objectives list
const ObjectivesChronologicalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 8px;
  overflow: hidden;
`;

// Individual objective item with left border accent
const ObjectiveItem = styled.div`
  padding: 16px 20px;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border-left: 3px solid ${({ theme }) => theme.colors.bgActive};
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    border-left-color: ${({ theme }) => theme.colors.textActive};
    background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  }
`;

const ObjectiveCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const ObjectiveIdText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textRegular};
  font-family: 'Courier New', monospace;
  letter-spacing: -0.2px;
  opacity: 0.7;
`;

const ObjectiveStatusBadge = styled.div<{
  status: 'exceeded' | 'met' | 'partial' | 'not_met' | 'not_tested';
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;

  ${({ status, theme }) => {
    switch (status) {
      case 'exceeded':
        return css`
          background-color: #1dea9920;
          border: 1px solid #1dea99;
          color: #1dea99;
        `;
      case 'met':
        return css`
          background-color: #1dea9920;
          border: 1px solid #1dea9980;
          color: #1dea99;
        `;
      case 'partial':
        return css`
          background-color: ${theme.colors.buttonSecondary}20;
          border: 1px solid ${theme.colors.buttonSecondary};
          color: ${theme.colors.buttonSecondary};
        `;
      case 'not_met':
        return css`
          background-color: ${theme.colors.buttonTertiary}20;
          border: 1px solid ${theme.colors.buttonTertiary};
          color: ${theme.colors.buttonTertiary};
        `;
      case 'not_tested':
        return css`
          background-color: ${theme.colors.textRegular}15;
          border: 1px solid ${theme.colors.textRegular}60;
          color: ${theme.colors.textRegular};
        `;
      default:
        return '';
    }
  }}
`;

const ObjectiveDescription = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.5;
`;

const ObjectiveQuestions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.8;
  flex-wrap: wrap;

  span {
    color: ${({ theme }) => theme.colors.textActive};
    font-weight: 500;
  }
`;

const QuestionIcon = styled.i`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  opacity: 0.7;
`;

const ClickableQuestionText = styled.span`
  color: ${({ theme }) => theme.colors.textActive};
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.buttonPrimary};
    text-decoration: underline;
  }
`;

export function ResultSkillDetailModal({
  skill,
  test,
  onClose,
  objectiveToScenarios,
  onQuestionClick,
}: ResultSkillDetailModalProps) {
  if (!skill) return null;

  // Build chronological objective map with descriptions from test.steps.objectives
  const objectivesChronological = buildObjectivesChronological(
    skill,
    test,
    objectiveToScenarios
  );

  return (
    <StickyHeaderModal
      title={
        <ModalTitleContainer>
          <ModalTitleMain>{skill.skillName}</ModalTitleMain>
          <ModalTitleCaption>
            Detailed skill performance breakdown
          </ModalTitleCaption>
        </ModalTitleContainer>
      }
      onClose={onClose}
    >
      <ModalContentSection>
        {/* Icon + Summary Metrics Row */}
        <ProminentMetricsCard>
          <MetricBox data-tooltip-id={`modal-score-${skill.skill}`}>
            <MetricBoxLabel>
              Score
              <InfoIcon />
            </MetricBoxLabel>
            <ScoreValueRow>
              <ModalScoreValue>{skill.score.toFixed(1)}</ModalScoreValue>
              <ScoreMax>/ 10</ScoreMax>
            </ScoreValueRow>
          </MetricBox>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="modal-score-${skill.skill}"]`}
          >
            <TooltipContent>
              <TooltipDetail>
                Overall performance score for this skill area
              </TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>

          <MetricBox data-tooltip-id={`modal-level-${skill.skill}`}>
            <MetricBoxLabel>
              Level
              <InfoIcon />
            </MetricBoxLabel>
            <ModalLevelValue>{skill.comparativeLevel}</ModalLevelValue>
          </MetricBox>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="modal-level-${skill.skill}"]`}
          >
            <TooltipContent>
              <TooltipDetail>
                Comparative proficiency level based on performance
              </TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>

          <MetricBox data-tooltip-id={`modal-confidence-${skill.skill}`}>
            <MetricBoxLabel>
              Confidence
              <InfoIcon />
            </MetricBoxLabel>
            <ScoreValueRow>
              <ModalConfidenceValue confidence={skill.confidence}>
                {Math.round(skill.confidence * 100)}
              </ModalConfidenceValue>
              <PercentageSign>%</PercentageSign>
            </ScoreValueRow>
          </MetricBox>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="modal-confidence-${skill.skill}"]`}
          >
            <TooltipContent>
              <TooltipDetail>
                Based on{' '}
                {Math.round(
                  ((skill.objectivesByStatus.exceeded.length +
                    skill.objectivesByStatus.met.length +
                    skill.objectivesByStatus.partial.length +
                    skill.objectivesByStatus.notMet.length) /
                    (skill.objectivesByStatus.exceeded.length +
                      skill.objectivesByStatus.met.length +
                      skill.objectivesByStatus.partial.length +
                      skill.objectivesByStatus.notMet.length +
                      skill.objectivesByStatus.notTested.length)) *
                    100
                )}
                % of objectives tested
              </TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>
        </ProminentMetricsCard>

        {/* Objectives Breakdown - Chronological */}
        <ObjectivesSection>
          <SectionTitleWithIcon>
            <InsightIcon className="fa fa-list-check" />
            Objectives Breakdown
          </SectionTitleWithIcon>
          <ObjectivesChronologicalList>
            {objectivesChronological.map((obj) => (
              <ObjectiveItem key={obj.id}>
                <ObjectiveCardHeader>
                  <ObjectiveIdText>{obj.id}</ObjectiveIdText>
                  <ObjectiveStatusBadge status={obj.status}>
                    {obj.status === 'exceeded'
                      ? 'Exceeded'
                      : obj.status === 'met'
                      ? 'Met'
                      : obj.status === 'partial'
                      ? 'Partial'
                      : obj.status === 'not_met'
                      ? 'Not Met'
                      : 'Not Tested'}
                  </ObjectiveStatusBadge>
                </ObjectiveCardHeader>
                <ObjectiveDescription>{obj.description}</ObjectiveDescription>
                <ObjectiveQuestions>
                  <QuestionIcon className="fa-regular fa-file-lines" />
                  {obj.testedIn.length > 0 ? (
                    <>
                      Tested in:{' '}
                      {obj.testedIn.map((testInfo, idx) => (
                        <React.Fragment
                          key={`${testInfo.scenarioLabel}-${testInfo.questionNumber}`}
                        >
                          <ClickableQuestionText
                            onClick={(e) => {
                              e.stopPropagation();
                              onQuestionClick(
                                testInfo.questionId,
                                testInfo.scenarioLabel
                              );
                            }}
                          >
                            {testInfo.scenarioLabel} - Question{' '}
                            {testInfo.questionNumber}
                          </ClickableQuestionText>
                          {idx < obj.testedIn.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </>
                  ) : (
                    <>Not present in any test scenarios</>
                  )}
                </ObjectiveQuestions>
              </ObjectiveItem>
            ))}
          </ObjectivesChronologicalList>
        </ObjectivesSection>

        {/* Key Insights - Moved to Bottom in 2-Column Grid */}
        <InsightsGrid>
          <InsightSection>
            <InsightTitle>
              <InsightIcon className="fa fa-medal" />
              Key Strengths
            </InsightTitle>
            <MarkdownWrapper md={skill.keyStrengths} />
          </InsightSection>

          <InsightSection>
            <InsightTitle>
              <InsightIcon className="fa fa-exclamation-triangle" />
              Development Areas
            </InsightTitle>
            <MarkdownWrapper md={skill.developmentAreas} />
          </InsightSection>
        </InsightsGrid>
      </ModalContentSection>
    </StickyHeaderModal>
  );
}
